export type PdfFontStyle = "normal" | "medium" | "italic" | "mediumitalic";

export interface iPdfTextRun {
	text: string;
	style: PdfFontStyle;
}

export interface iPdfTableRow {
	cells: iPdfTextRun[][];
	isHeader: boolean;
}

export interface iPdfBlock {
	kind: "heading" | "paragraph" | "list-item" | "spacer" | "table";
	level?: number;
	runs: iPdfTextRun[];
	rows?: iPdfTableRow[];
}

const BLOCK_TAGS = new Set([
	"H1",
	"H2",
	"H3",
	"H4",
	"P",
	"LI",
	"UL",
	"OL",
	"BLOCKQUOTE",
	"TABLE",
	"THEAD",
	"TBODY",
	"TR",
	"TH",
	"TD",
	"BR",
]);

const collapseWhitespace = (value: string): string => value.replace(/\s+/g, " ");

const mergeRuns = (runs: iPdfTextRun[]): iPdfTextRun[] => {
	const merged: iPdfTextRun[] = [];

	for (const run of runs) {
		if (!run.text) {
			continue;
		}

		const previous = merged[merged.length - 1];
		if (previous && previous.style === run.style) {
			previous.text += run.text;
			continue;
		}

		merged.push({ ...run });
	}

	return merged;
};

const collectRuns = (node: Node, style: PdfFontStyle = "normal"): iPdfTextRun[] => {
	if (node.nodeType === Node.TEXT_NODE) {
		const text = collapseWhitespace(node.textContent ?? "");
		return text ? [{ text, style }] : [];
	}

	if (!(node instanceof HTMLElement)) {
		return [];
	}

	const tag = node.tagName;
	if (tag === "BR") {
		return [{ text: "\n", style }];
	}

	let nextStyle = style;
	if (tag === "STRONG" || tag === "B") {
		// Headings and emphasis use medium — never bold/semibold.
		nextStyle = style === "italic" || style === "mediumitalic" ? "mediumitalic" : "medium";
	} else if (tag === "EM" || tag === "I") {
		nextStyle = style === "medium" || style === "mediumitalic" ? "mediumitalic" : "italic";
	}

	const runs: iPdfTextRun[] = [];
	node.childNodes.forEach((child) => {
		runs.push(...collectRuns(child, nextStyle));
	});
	return runs;
};

const pushBlock = (blocks: iPdfBlock[], kind: iPdfBlock["kind"], runs: iPdfTextRun[], level = 0) => {
	const cleaned = mergeRuns(
		runs
			.map((run) => ({ ...run, text: run.text.replace(/\u00a0/g, " ") }))
			.filter((run) => run.text.length > 0)
	);

	if (kind === "spacer") {
		blocks.push({ kind, level, runs: [] });
		return;
	}

	// CMS inserts empty / &nbsp; paragraphs between clauses — skip them so the
	// PDF does not accumulate large blank bands.
	if (cleaned.length === 0 || cleaned.every((run) => !run.text.trim())) {
		return;
	}

	blocks.push({ kind, level, runs: cleaned });
};

const parseTable = (table: HTMLElement): iPdfBlock | null => {
	const rows: iPdfTableRow[] = [];

	Array.from(table.querySelectorAll("tr")).forEach((tr) => {
		const cells: iPdfTextRun[][] = [];
		let isHeader = false;

		Array.from(tr.children).forEach((cell) => {
			if (!(cell instanceof HTMLElement)) {
				return;
			}
			if (cell.tagName !== "TH" && cell.tagName !== "TD") {
				return;
			}

			if (cell.tagName === "TH") {
				isHeader = true;
			}

			const runs = mergeRuns(
				collectRuns(cell)
					.map((run) => ({ ...run, text: run.text.replace(/\u00a0/g, " ") }))
					.filter((run) => run.text.trim().length > 0)
			);
			cells.push(runs.length ? runs : [{ text: " ", style: "normal" }]);
		});

		if (cells.length > 0) {
			rows.push({ cells, isHeader });
		}
	});

	if (rows.length === 0) {
		return null;
	}

	return { kind: "table", runs: [], rows };
};

/**
 * Walks sanitized CMS HTML into printable blocks. Pure — no jsPDF / canvas.
 * Medium weight is applied only where the CMS uses <strong>/<b>.
 */
export const cmsHtmlToPdfBlocks = (html: string): iPdfBlock[] => {
	const document = new DOMParser().parseFromString(`<div id="root">${html}</div>`, "text/html");
	const root = document.getElementById("root");
	if (!root) {
		return [];
	}

	const blocks: iPdfBlock[] = [];

	const walkList = (list: HTMLElement, depth: number) => {
		const isOrdered = list.tagName === "OL";
		const startAttr = Number(list.getAttribute("start") ?? "1");
		let index = Number.isFinite(startAttr) && startAttr > 0 ? startAttr : 1;

		Array.from(list.children).forEach((child) => {
			if (!(child instanceof HTMLElement) || child.tagName !== "LI") {
				return;
			}

			const marker = isOrdered ? `${index}. ` : "• ";
			index += 1;

			const nestedLists = Array.from(child.children).filter(
				(el): el is HTMLElement => el instanceof HTMLElement && (el.tagName === "UL" || el.tagName === "OL")
			);

			const itemClone = child.cloneNode(true) as HTMLElement;
			itemClone.querySelectorAll("ul, ol").forEach((nested) => nested.remove());

			const runs = collectRuns(itemClone);
			pushBlock(blocks, "list-item", [{ text: marker, style: "normal" }, ...runs], depth);

			nestedLists.forEach((nested) => walkList(nested, depth + 1));
		});
	};

	const walk = (node: Node) => {
		if (node.nodeType === Node.TEXT_NODE) {
			const text = collapseWhitespace(node.textContent ?? "").trim();
			if (text) {
				pushBlock(blocks, "paragraph", [{ text, style: "normal" }]);
			}
			return;
		}

		if (!(node instanceof HTMLElement)) {
			return;
		}

		const tag = node.tagName;

		if (tag === "H1" || tag === "H2" || tag === "H3" || tag === "H4") {
			pushBlock(blocks, "heading", collectRuns(node), Number(tag.charAt(1)));
			return;
		}

		if (tag === "TABLE") {
			const tableBlock = parseTable(node);
			if (tableBlock) {
				blocks.push(tableBlock);
			}
			return;
		}

		if (tag === "P") {
			pushBlock(blocks, "paragraph", collectRuns(node));
			return;
		}

		if (tag === "UL" || tag === "OL") {
			walkList(node, 0);
			return;
		}

		if (tag === "BR") {
			pushBlock(blocks, "spacer", []);
			return;
		}

		if (tag === "BLOCKQUOTE" || tag === "THEAD" || tag === "TBODY" || tag === "TR" || tag === "DIV") {
			node.childNodes.forEach((child) => walk(child));
			return;
		}

		if (tag === "TH" || tag === "TD") {
			pushBlock(blocks, "paragraph", collectRuns(node));
			return;
		}

		if (!BLOCK_TAGS.has(tag)) {
			const runs = collectRuns(node);
			if (runs.length) {
				pushBlock(blocks, "paragraph", runs);
			}
			return;
		}

		node.childNodes.forEach((child) => walk(child));
	};

	root.childNodes.forEach((child) => walk(child));
	return blocks;
};
